import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface TypewriterProps {
  questions: string[];
}

const Typewriter: React.FC<TypewriterProps> = ({ questions }) => {
  const [index, setIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [typing, setTyping] = useState(true);

  useEffect(() => {
    const full = questions[index];
    let i = 0;
    let timeout: ReturnType<typeof setTimeout>;

    if (typing) {
      const type = () => {
        if (i <= full.length) {
          setDisplayed(full.slice(0, i));
          i++;
          timeout = setTimeout(type, 42);
        } else {
          timeout = setTimeout(() => setTyping(false), 2400);
        }
      };
      type();
    } else {
      const erase = () => {
        if (i >= 0) {
          setDisplayed(full.slice(0, i));
          i--;
          timeout = setTimeout(erase, 22);
        } else {
          setIndex(idx => (idx + 1) % questions.length);
          setTyping(true);
        }
      };
      i = full.length;
      erase();
    }

    return () => clearTimeout(timeout);
  }, [index, typing]);

  return (
    <div style={{ height: '4rem', display: 'flex', alignItems: 'flex-start', overflow: 'hidden' }}>
      <p className="text-indigo-600 font-semibold text-lg sm:text-xl" style={{ margin: 0 }}>
        {displayed}
        <span style={{ display: 'inline-block', width: '2px', height: '1.1em', background: '#4f46e5', marginLeft: '2px', verticalAlign: 'middle', animation: 'blink 1s step-end infinite' }} />
      </p>
    </div>
  );
};

interface SectionProps {
  tag: string;
  title: string;
  description: string;
  questions: string[];
  cta: string;
  path: string;
  flipped?: boolean;
  emoji: string;
}

const FeatureSection: React.FC<SectionProps> = ({ tag, title, description, questions, cta, path, flipped, emoji }) => {
  const navigate = useNavigate();

  return (
    <section className={`px-6 py-16 ${flipped ? 'bg-white' : 'bg-gray-50'}`}>
      <div className={`max-w-5xl mx-auto flex flex-col ${flipped ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12`}>
        {/* Text side */}
        <div className="flex-1">
          <p className="text-indigo-500 text-xs font-semibold uppercase tracking-widest mb-3">{tag}</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{title}</h2>
          <p className="text-gray-500 text-base leading-relaxed mb-8">{description}</p>
          <button
            onClick={() => navigate(path)}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors text-sm"
          >
            {cta} →
          </button>
        </div>

        {/* Typewriter side */}
        <div className="flex-1 w-full">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
            <div className="text-3xl mb-4">{emoji}</div>
            <p className="text-gray-400 text-sm mb-2">People are asking...</p>
            <Typewriter questions={questions} />
          </div>
        </div>
      </div>
    </section>
  );
};

const SECTIONS: SectionProps[] = [
  {
    tag: 'Groups',
    emoji: '👥',
    title: 'Find your people',
    description: 'Discover groups of people who share your background, interests and city. From cultural associations to sports clubs — your community is waiting.',
    questions: [
      'Is there an Indian group in Munich?',
      'Any badminton club in Berlin?',
      'Hindi-speaking parents group in Frankfurt?',
      'South Asian professionals in Hamburg?',
    ],
    cta: 'Explore Groups',
    path: '/groups',
    flipped: false,
  },
  {
    tag: 'Events',
    emoji: '📅',
    title: 'Never miss a moment',
    description: 'Browse community events near you — cultural celebrations, meetups, festivals and more. RSVP and get reminded so you never forget.',
    questions: [
      'Any Diwali events this weekend in Berlin?',
      'Holi celebration near me?',
      'Indian food festival in Munich?',
      'Bollywood night in Frankfurt?',
    ],
    cta: 'Explore Events',
    path: '/events',
    flipped: true,
  },
  {
    tag: 'Services',
    emoji: '🛠️',
    title: 'Trusted help, in your language',
    description: 'Find professionals from your community — doctors, lawyers, accountants, tutors and more. People who understand your culture and speak your language.',
    questions: [
      'Hindi-speaking doctor in Frankfurt?',
      'Indian accountant in Berlin?',
      'Gujarati lawyer near me?',
      'Desi tutor for my kids in Munich?',
    ],
    cta: 'Explore Services',
    path: '/services',
    flipped: false,
  },
  {
    tag: 'Marketplace',
    emoji: '🛒',
    title: 'Buy and sell within your community',
    description: 'List items you no longer need or find what you\'re looking for — all within a trusted community of people you can relate to.',
    questions: [
      'Selling my Indian groceries stash in Berlin?',
      'Looking for a second-hand pressure cooker?',
      'Anyone selling a sofa near Frankfurt?',
      'Where to buy Indian spices in Munich?',
    ],
    cta: 'Browse Marketplace',
    path: '/marketplace',
    flipped: true,
  },
];

export const FeatureSections: React.FC = () => {
  return (
    <>
      <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
      {SECTIONS.map((s, i) => (
        <FeatureSection key={i} {...s} />
      ))}
    </>
  );
};
